import React, { FC, useState } from 'react';
import type { ChatMessage } from '../../types';
import { ReactComponent as CalendarPeriodIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/calendar-period.svg';
import { ReactComponent as AttachedDocsIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/attached-docs.svg';
import { ReactComponent as WhatsIncludedIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/whats-included.svg';
import { ReactComponent as ChevronRightIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/chevron-right.svg';
import './PackageOfferBubble.scss';

interface PackageOfferBubbleProps {
  message: ChatMessage;
  onOpenDocument?: (docType: string) => void;
}

const DEFAULT_INCLUDED_ITEMS = [
  'Pre-session questionnaire to understand your goals, fitness level, and schedule',
  'Session programme tailored to you, integrating AI-based form tracking & MPT Score',
  'Real-time feedback from a certified PT',
  'Session-specific MPT Score (accuracy, goal-based calorie burn, reps completed)',
  'Access to personal trainer notes & AI session history',
  'Chat/message support in the app for quick form checks or questions (during package period)',
];

export const PackageOfferBubble: FC<PackageOfferBubbleProps> = ({ message, onOpenDocument }) => {
  const [isIncludedOpen, setIsIncludedOpen] = useState(true);

  const packageName = message.package_name || 'Basic Package';
  const price = message.price !== undefined ? message.price : 95;
  const currencySymbol = message.currency_symbol || (message.currency === 'GBP' ? '£' : '$');
  const duration = message.duration || '4 weeks';
  const startDate = message.start_date || '28 Sep 2026';
  const isConfirmed =
    message.offer_status === 'confirmed' ||
    message.offer_status === 'accepted' ||
    message.offer_status === 'active';

  const statusLabel = isConfirmed
    ? 'Confirmed'
    : message.offer_status === 'declined'
      ? 'Declined'
      : 'Awaiting response';

  const items =
    message.whats_included && message.whats_included.length > 0
      ? message.whats_included
      : DEFAULT_INCLUDED_ITEMS;

  const handleDocClick = (docType: string) => {
    if (onOpenDocument) {
      onOpenDocument(docType);
    } else {
      window.open(
        `/onboarding-trainer/dashboard/profile?tab=packages&view=template&type=${docType}`,
        '_blank',
      );
    }
  };

  return (
    <div className="package-offer-bubble-card">
      {/* Top Status Badge */}
      <div className={`offer-status-badge ${isConfirmed ? 'confirmed' : ''}`}>
        <span className="status-dot" />
        <span className="status-text">{statusLabel}</span>
      </div>

      {/* Package Header: Title, Price, Subtitle */}
      <div className="offer-pkg-header">
        <h3 className="offer-pkg-title">{packageName}</h3>
        <div className="offer-price-val">{`${currencySymbol}${price}`}</div>
        <div className="offer-duration-meta">
          <span>{`${duration} • 28 Sep - 25 Sep 2026`}</span>
        </div>
      </div>

      {/* Program Start Date */}
      <div className="offer-start-date-box">
        <div className="start-date-left">
          <div className="cal-icon-wrap">
            <CalendarPeriodIcon />
          </div>
          <div className="date-text-col">
            <span className="date-label">{'Program Start Date'}</span>
            <span className="date-sub">{'Training program will be ready on this date'}</span>
          </div>
        </div>
        <div className="start-date-display">{startDate}</div>
      </div>

      {/* What's Included */}
      <div className="offer-included-box">
        <div className="included-header" onClick={() => setIsIncludedOpen(!isIncludedOpen)}>
          <div className="included-title-row">
            <div className="fire-icon-wrap">
              <WhatsIncludedIcon className="fire-icon" />
            </div>
            <span className="included-title">{"What's included"}</span>
          </div>
          <svg
            className={`chevron-icon ${isIncludedOpen ? 'open' : ''}`}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </div>

        <div className={`included-checklist-wrapper ${isIncludedOpen ? 'expanded' : 'collapsed'}`}>
          <div className="included-checklist">
            {items.map((text, idx) => (
              <div key={idx} className="included-check-item">
                <div className="check-icon-circle">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="9" fill="#FFA100" />
                    <path
                      d="M5.5 9.2L7.8 11.5L12.5 6.5"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="check-desc">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attached Documents */}
      <div className="offer-attached-docs">
        <span className="docs-label">{'Attached documents'}</span>
        <div className="docs-card-list">
          <div className="doc-card-row" onClick={() => handleDocClick('checkin_policy')}>
            <div className="doc-left">
              <AttachedDocsIcon className="doc-icon" />
              <span>{'Check-in policy'}</span>
            </div>
            <ChevronRightIcon className="doc-chevron" />
          </div>

          <div className="doc-card-row" onClick={() => handleDocClick('contractual_terms')}>
            <div className="doc-left">
              <AttachedDocsIcon className="doc-icon" />
              <span>{'Contractual terms'}</span>
            </div>
            <ChevronRightIcon className="doc-chevron" />
          </div>

          <div className="doc-card-row" onClick={() => handleDocClick('cancellation_policy')}>
            <div className="doc-left">
              <AttachedDocsIcon className="doc-icon" />
              <span>{'Cancellation and unused policy'}</span>
            </div>
            <ChevronRightIcon className="doc-chevron" />
          </div>
        </div>
      </div>
    </div>
  );
};
